import { forwardRef } from 'react';
import { Snackbar as MuiSnackbar, IconButton, Box } from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import type { SnackbarProps } from './Snackbar.props';

export const Snackbar = forwardRef<HTMLDivElement, SnackbarProps>(
  (
    {
      open,
      message,
      variant = 'info',
      onClose,
      autoHideDuration = 4000,
      anchorOrigin = { vertical: 'bottom', horizontal: 'center' },
      ...rest
    },
    ref
  ) => {
    // Get variant-specific icon
    const getVariantIcon = () => {
      switch (variant) {
        case 'error':
          return <CancelOutlinedIcon sx={{ fontSize: 24 }} />;
        case 'warning':
          return <WarningAmberOutlinedIcon sx={{ fontSize: 24 }} />;
        case 'success':
          return <CheckCircleOutlinedIcon sx={{ fontSize: 24 }} />;
        case 'info':
        default:
          return <InfoOutlinedIcon sx={{ fontSize: 24 }} />;
      }
    };

    const variantIcon = getVariantIcon();

    return (
      <MuiSnackbar
        ref={ref}
        open={open}
        onClose={onClose}
        autoHideDuration={autoHideDuration}
        anchorOrigin={anchorOrigin}
        {...rest}
        message={
          <Box className="snackbar-message-container" data-variant={variant}>
            {/* Icon */}
            <Box className="snackbar-icon">{variantIcon}</Box>

            {/* Message */}
            <Box className="snackbar-text">{message}</Box>

            {/* Close button */}
            {onClose && (
              <IconButton
                className="snackbar-close-button"
                onClick={(e) => {
                  // Call onClose with 'closeClick' reason to distinguish from 'clickaway'
                  // 'clickaway' is reserved for clicks outside the snackbar and may be blocked
                  onClose(
                    e,
                    'closeClick' as 'timeout' | 'clickaway' | 'escapeKeyDown'
                  );
                }}
              >
                <CloseOutlinedIcon sx={{ fontSize: 24 }} />
              </IconButton>
            )}
          </Box>
        }
      />
    );
  }
);

Snackbar.displayName = 'Snackbar';

export default Snackbar;
